import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tabs, Card, Typography, Tag, Space, Row, Col,
  notification, Popconfirm, Tooltip, Input, DatePicker, Select
} from 'antd';
import {
  ReloadOutlined, FileAddOutlined, FallOutlined, StopOutlined, EyeOutlined, SearchOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import billingService from '../../../services/billingService';
import { formatMoney } from '../Orders/orderFormHelpers';
import DispatchDetailDrawer from './DispatchDetailDrawer';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// La operación es en Colombia: "hoy" no puede depender de la zona del navegador.
const BUSINESS_TIMEZONE = 'America/Bogota';

// La pestaña activa va en la URL (?tab=...) para que al volver se abra la misma.
const TABS = { DISPATCHES: 'despachos', INVOICES: 'facturas' };

const formatDate = (value) =>
  value ? dayjs(value.slice(0, 10)).format('DD/MM/YYYY') : '-';

const INVOICE_STATUS_TAG = {
  ISSUED: { color: 'success', label: 'Emitida' },
  VOID: { color: 'error', label: 'Anulada' },
};

// billing_status lo calcula el backend; solo PENDING se puede facturar.
const DISPATCH_STATUS_TAG = {
  PENDING: { color: 'warning', label: 'Pendiente' },
  INVOICED: { color: 'success', label: 'Facturado' },
  VOID: { color: 'error', label: 'Anulado' },
};

// Las búsquedas puntuales también respetan las fechas: si no encuentran nada,
// lo más probable es que el documento sea más viejo que el rango.
const OUT_OF_RANGE_TEXT =
  'No se encontró en el rango de fechas seleccionado. Amplía las fechas para buscar más atrás.';

// Ambas pestañas arrancan de hoy a hoy + 2 días, inclusive.
const defaultFilters = () => {
  const today = dayjs().tz(BUSINESS_TIMEZONE);
  return {
    search: '',
    date_start: today.format('YYYY-MM-DD'),
    date_end: today.add(2, 'day').format('YYYY-MM-DD'),
  };
};

// Query params for the list endpoints; empty filters are left out.
const filterParams = (filters) => {
  const params = {};
  if (filters.search) params.search = filters.search;
  if (filters.date_start) params.date_start = filters.date_start;
  if (filters.date_end) params.date_end = filters.date_end;
  return params;
};

// `children` adds tab-specific filters after the shared ones.
const FilterBar = ({ filters, onChange, searchLabel = 'Buscar', searchPlaceholder, children }) => (
  <Row gutter={[16, 16]}>
    <Col xs={24} sm={12} md={8}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{searchLabel}</Text>
      <Input.Search
        placeholder={searchPlaceholder}
        allowClear
        onSearch={(value) => onChange({ search: value })}
        enterButton={<Button icon={<SearchOutlined />} />}
      />
    </Col>
    <Col xs={24} sm={12} md={8}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Fecha</Text>
      <RangePicker
        style={{ width: '100%' }}
        value={[
          filters.date_start ? dayjs(filters.date_start) : null,
          filters.date_end ? dayjs(filters.date_end) : null,
        ]}
        onChange={(dates) => onChange(dates && dates.length === 2
          ? { date_start: dates[0].format('YYYY-MM-DD'), date_end: dates[1].format('YYYY-MM-DD') }
          : { date_start: null, date_end: null })}
        placeholder={['Fecha desde', 'Fecha hasta']}
      />
    </Col>
    {children}
  </Row>
);

const BillingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === TABS.INVOICES ? TABS.INVOICES : TABS.DISPATCHES;

  const [dispatches, setDispatches] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loadingDispatches, setLoadingDispatches] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [issuing, setIssuing] = useState(false);
  const [detailDispatchId, setDetailDispatchId] = useState(null);

  const [dispatchFilters, setDispatchFilters] = useState(() => ({ ...defaultFilters(), billing_status: 'PENDING', fito: '' }));
  const [dispatchPagination, setDispatchPagination] = useState({ current: 1, pageSize: 25 });
  const [dispatchTotal, setDispatchTotal] = useState(0);
  // Voided invoices stay out until the status filter is changed.
  const [invoiceFilters, setInvoiceFilters] = useState(() => ({ ...defaultFilters(), status: 'ISSUED', fito: '' }));
  const [invoicePagination, setInvoicePagination] = useState({ current: 1, pageSize: 25 });
  const [invoiceTotal, setInvoiceTotal] = useState(0);

  const fetchDispatches = useCallback(async () => {
    setLoadingDispatches(true);
    try {
      const response = await billingService.listarDespachos({
        ...filterParams(dispatchFilters),
        // ALL no envía estado: el backend devuelve todos.
        ...(dispatchFilters.billing_status !== 'ALL' && { billing_status: dispatchFilters.billing_status }),
        ...(dispatchFilters.fito && { fito: dispatchFilters.fito }),
        page: dispatchPagination.current,
        limit: dispatchPagination.pageSize,
      });
      setDispatches(response?.data || []);
      setDispatchTotal(response?.total ?? 0);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.mensaje || 'No se pudieron cargar los despachos.',
      });
    } finally {
      setLoadingDispatches(false);
    }
  }, [dispatchFilters, dispatchPagination]);

  const fetchInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const response = await billingService.listarFacturas({
        ...filterParams(invoiceFilters),
        // ALL sends no status, so the backend returns every invoice.
        ...(invoiceFilters.status !== 'ALL' && { status: invoiceFilters.status }),
        ...(invoiceFilters.fito && { fito: invoiceFilters.fito }),
        page: invoicePagination.current,
        limit: invoicePagination.pageSize,
      });
      setInvoices(response?.data || []);
      setInvoiceTotal(response?.total ?? 0);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.mensaje || 'No se pudieron cargar las facturas.',
      });
    } finally {
      setLoadingInvoices(false);
    }
  }, [invoiceFilters, invoicePagination]);

  useEffect(() => { fetchDispatches(); }, [fetchDispatches]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // A selection made under other filters may no longer be visible: start over.
  const handleDispatchFiltersChange = (changes) => {
    setDispatchFilters((prev) => ({ ...prev, ...changes }));
    setDispatchPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedKeys([]);
  };

  const handleInvoiceFiltersChange = (changes) => {
    setInvoiceFilters((prev) => ({ ...prev, ...changes }));
    setInvoicePagination((prev) => ({ ...prev, current: 1 }));
  };

  const selectedDispatches = dispatches.filter((d) => selectedKeys.includes(d.dispatch_id));
  // Solo se suma si todo está en la misma moneda: un total de USD con COP no
  // dice nada.
  const selectedCurrencies = [...new Set(selectedDispatches.map((d) => d.order?.client?.currency))];
  const selectedTotal = selectedCurrencies.length === 1
    ? formatMoney(selectedDispatches.reduce((sum, d) => sum + Number(d.total || 0), 0), selectedCurrencies[0])
    : 'Monedas mixtas';

  // Una factura agrupada es de un solo cliente; por separado no importa. El
  // backend lo valida igual.
  const mixedClients = new Set(selectedDispatches.map((d) => d.order?.client?.client_id)).size > 1;

  const handleIssue = async () => {
    setIssuing(true);
    try {
      const response = await billingService.crearFactura(selectedKeys);
      notification.success({
        message: 'Factura emitida',
        description: `Factura ${response?.data?.display_number} creada exitosamente.`,
      });
      setSelectedKeys([]);
      await Promise.all([fetchDispatches(), fetchInvoices()]);
    } catch (error) {
      notification.error({
        message: 'No se pudo emitir la factura',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    } finally {
      setIssuing(false);
    }
  };

  const handleIssueSeparately = async () => {
    setIssuing(true);
    try {
      const response = await billingService.crearFacturasSeparadas(selectedKeys);
      const numbers = (response?.data || []).map((invoice) => invoice.display_number);
      notification.success({
        message: `${numbers.length} facturas emitidas`,
        description: `Facturas ${numbers.join(', ')} creadas exitosamente.`,
      });
      setSelectedKeys([]);
      await Promise.all([fetchDispatches(), fetchInvoices()]);
    } catch (error) {
      notification.error({
        message: 'No se pudieron emitir las facturas',
        description: error?.response?.data?.mensaje || 'Error inesperado. No se creó ninguna factura.',
      });
    } finally {
      setIssuing(false);
    }
  };

  // The backend answers 409 while the invoice has active credit notes; its
  // message is shown as is. Once voided, its dispatches return to the other tab.
  const handleVoidInvoice = async (record) => {
    try {
      await billingService.anularFactura(record.invoice_id);
      notification.success({
        message: 'Factura anulada',
        description: `La factura ${record.display_number} quedó anulada y sus despachos vuelven a estar sin facturar.`,
      });
      await Promise.all([fetchDispatches(), fetchInvoices()]);
    } catch (error) {
      notification.error({
        message: 'No se pudo anular la factura',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    }
  };

  const dispatchColumns = [
    {
      title: 'Despacho',
      dataIndex: 'number',
      key: 'number',
      render: (_, record) => <Text strong>{record.display_number || '-'}</Text>,
    },
    {
      title: 'Orden',
      key: 'order_number',
      render: (_, record) => record.order?.display_number || '-',
    },
    {
      title: 'Cliente',
      key: 'client',
      render: (_, record) => record.order?.client?.name || '-',
    },
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
      render: formatDate,
    },
    {
      title: 'Fito',
      dataIndex: 'fito_display',
      key: 'fito_display',
    },
    {
      title: 'Notas',
      key: 'notes',
      render: (_, record) => record.order?.notes || null,
    },
    {
      title: 'Estado',
      dataIndex: 'billing_status',
      key: 'billing_status',
      render: (status) => {
        const tag = DISPATCH_STATUS_TAG[status];
        return tag ? <Tag color={tag.color}>{tag.label}</Tag> : status;
      },
    },
    {
      title: 'Líneas',
      key: 'lines',
      align: 'right',
      render: (_, record) => record.details?.length ?? 0,
    },
    {
      title: 'Valor',
      key: 'total',
      align: 'right',
      render: (_, record) => (
        <Text strong>{formatMoney(record.total, record.order?.client?.currency)}</Text>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Ver detalle">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailDispatchId(record.dispatch_id)}
            style={{ borderColor: '#d9d9d9', color: 'var(--fc-accent)' }}
          />
        </Tooltip>
      ),
    },
  ];

  const invoiceColumns = [
    {
      title: 'Nº Factura',
      dataIndex: 'display_number',
      key: 'number',
      render: (value) => <Text strong>{value || '-'}</Text>,
    },
    {
      title: 'Cliente',
      key: 'client',
      render: (_, record) => record.client?.name || '-',
    },
    {
      title: 'Fito',
      dataIndex: 'fitos',
      key: 'fitos',
      render: (fitos) => (fitos || []).join(', '),
    },
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
      render: formatDate,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const tag = INVOICE_STATUS_TAG[status];
        return tag ? <Tag color={tag.color}>{tag.label}</Tag> : status;
      },
    },
    {
      title: 'Total',
      key: 'total',
      align: 'right',
      render: (_, record) => formatMoney(record.total, record.currency),
    },
    {
      title: 'Bajas',
      key: 'credited',
      align: 'right',
      render: (_, record) =>
        Number(record.credited) > 0
          ? <Text type="danger">- {formatMoney(record.credited, record.currency)}</Text>
          : <Text type="secondary">-</Text>,
    },
    {
      title: 'Saldo',
      key: 'balance',
      align: 'right',
      render: (_, record) => (
        <Text strong>{formatMoney(record.balance, record.currency)}</Text>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalle">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/sales/invoices/${record.invoice_id}`)}
              style={{ borderColor: '#d9d9d9', color: 'var(--fc-accent)' }}
            />
          </Tooltip>
          {/* A voided invoice takes no credit notes and cannot be voided again. */}
          {record.status === 'ISSUED' && (
            <>
              <Tooltip title="Registrar baja">
                <Button
                  size="small"
                  icon={<FallOutlined />}
                  onClick={() => navigate(`/sales/credit-notes?invoice_id=${record.invoice_id}`)}
                  style={{ borderColor: '#d9d9d9', color: '#8c8c8c' }}
                />
              </Tooltip>
              <Popconfirm
                title="Anular factura"
                description={`¿Anular la factura ${record.display_number}? Sus despachos quedarán sin facturar.`}
                okText="Anular"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleVoidInvoice(record)}
              >
                <Tooltip title="Anular factura">
                  <Button size="small" danger icon={<StopOutlined />} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  const pendingTab = (
    <>
      <div style={{ marginBottom: 16 }}>
        <FilterBar
          filters={dispatchFilters}
          onChange={handleDispatchFiltersChange}
          searchLabel="N° de orden"
          searchPlaceholder="Buscar N° de orden"
        >
          <Col xs={24} sm={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Estado</Text>
            <Select
              style={{ width: '100%' }}
              value={dispatchFilters.billing_status}
              onChange={(value) => handleDispatchFiltersChange({ billing_status: value })}
              options={[
                { value: 'ALL', label: 'Todos' },
                { value: 'PENDING', label: 'Pendiente' },
                { value: 'INVOICED', label: 'Facturado' },
                { value: 'VOID', label: 'Anulado' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Fito</Text>
            <Input.Search
              placeholder="Buscar fito"
              allowClear
              onSearch={(value) => handleDispatchFiltersChange({ fito: value.trim() })}
              enterButton={<Button icon={<SearchOutlined />} />}
            />
          </Col>
        </FilterBar>
      </div>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          {selectedKeys.length > 0 && (
            <Text>
              {selectedKeys.length} despacho(s) seleccionado(s) ·{' '}
              <Text strong>{selectedTotal}</Text>
            </Text>
          )}
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchDispatches} />
            {selectedKeys.length <= 1 ? (
              <Popconfirm
                title="Emitir factura"
                description="Se creará 1 factura."
                okText="Emitir"
                cancelText="Cancelar"
                onConfirm={handleIssue}
                disabled={selectedKeys.length === 0}
              >
                <Button
                  type="primary"
                  icon={<FileAddOutlined />}
                  disabled={selectedKeys.length === 0}
                  loading={issuing}
                  style={{ backgroundColor: 'var(--fc-accent)' }}
                >
                  Facturar
                </Button>
              </Popconfirm>
            ) : (
              <>
                <Tooltip title={mixedClients ? 'Solo se agrupan despachos del mismo cliente' : null}>
                  <Popconfirm
                    title="Facturar agrupado"
                    description={`Se creará 1 factura con los ${selectedKeys.length} despachos.`}
                    okText="Emitir"
                    cancelText="Cancelar"
                    onConfirm={handleIssue}
                    disabled={mixedClients}
                  >
                    <Button
                      type="primary"
                      icon={<FileAddOutlined />}
                      disabled={mixedClients}
                      loading={issuing}
                      style={mixedClients ? undefined : { backgroundColor: 'var(--fc-accent)' }}
                    >
                      Facturar agrupado
                    </Button>
                  </Popconfirm>
                </Tooltip>
                <Popconfirm
                  title="Facturar por separado"
                  description={`Se crearán ${selectedKeys.length} facturas, una por despacho.`}
                  okText="Emitir"
                  cancelText="Cancelar"
                  onConfirm={handleIssueSeparately}
                >
                  <Button icon={<FileAddOutlined />} loading={issuing}>
                    Facturar por separado
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        columns={dispatchColumns}
        dataSource={dispatches}
        rowKey={(record) => record.dispatch_id}
        loading={loadingDispatches}
        scroll={{ x: 800 }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          // Ya no se bloquean los otros clientes: por separado se pueden mezclar.
          getCheckboxProps: (record) => ({ disabled: record.billing_status !== 'PENDING' }),
        }}
        pagination={{
          current: dispatchPagination.current,
          pageSize: dispatchPagination.pageSize,
          total: dispatchTotal,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100'],
          // La selección se limita a la página visible: el total y el cliente
          // se calculan con los despachos cargados.
          onChange: (page, pageSize) => {
            setDispatchPagination({ current: page, pageSize });
            setSelectedKeys([]);
          },
        }}
        locale={{
          emptyText: dispatchFilters.fito || dispatchFilters.search ? OUT_OF_RANGE_TEXT : 'No hay despachos',
        }}
      />
    </>
  );

  const issuedTab = (
    <>
      <Row justify="space-between" align="bottom" style={{ marginBottom: 16 }} gutter={[16, 16]}>
        <Col flex="auto">
          <FilterBar
            filters={invoiceFilters}
            onChange={handleInvoiceFiltersChange}
            searchPlaceholder="Buscar número de factura"
          >
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Estado</Text>
              <Select
                style={{ width: '100%' }}
                value={invoiceFilters.status}
                onChange={(value) => handleInvoiceFiltersChange({ status: value })}
                options={[
                  { value: 'ALL', label: 'Todas' },
                  { value: 'ISSUED', label: 'Emitida' },
                  { value: 'VOID', label: 'Anulada' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Fito</Text>
              <Input.Search
                placeholder="Buscar fito"
                allowClear
                onSearch={(value) => handleInvoiceFiltersChange({ fito: value.trim() })}
                enterButton={<Button icon={<SearchOutlined />} />}
              />
            </Col>
          </FilterBar>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchInvoices} />
        </Col>
      </Row>

      <Table
        columns={invoiceColumns}
        dataSource={invoices}
        rowKey={(record) => record.invoice_id}
        loading={loadingInvoices}
        scroll={{ x: 900 }}
        pagination={{
          current: invoicePagination.current,
          pageSize: invoicePagination.pageSize,
          total: invoiceTotal,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100'],
          onChange: (page, pageSize) => setInvoicePagination({ current: page, pageSize }),
        }}
        locale={{ emptyText: invoiceFilters.fito ? OUT_OF_RANGE_TEXT : 'No hay facturas emitidas' }}
      />
    </>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0, color: '#595959', fontWeight: 600 }}>
            Facturación
          </Title>
        </Col>
      </Row>

      <Card>
        {/* replace: cambiar de pestaña no apila historial, así "atrás" sale de la pantalla. */}
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
          items={[
            {
              key: TABS.DISPATCHES,
              label: 'Despachos',
              children: pendingTab,
            },
            {
              key: TABS.INVOICES,
              label: 'Facturas emitidas',
              children: issuedTab,
            },
          ]}
        />
      </Card>

      <DispatchDetailDrawer
        dispatchId={detailDispatchId}
        open={detailDispatchId !== null}
        onClose={() => setDetailDispatchId(null)}
      />
    </div>
  );
};

export default BillingPage;
